import React, { useState, useEffect, useMemo } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getFeaturesForMetro } from '../data/features';
import { getMetroById, METROS } from '../data/metros';
import { geocodeBarAddress, inferMetroForRequest } from '../utils/barGeocode';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  Beer,
  MessageCircle,
  ClipboardList,
  Activity,
  ArrowLeft,
  TrendingUp,
  Clock,
  Check,
  X,
  Trash2,
  Eye,
  EyeOff,
  Edit,
  Plus,
  Search,
  RefreshCw,
  Shield,
  BarChart3,
  Calendar,
  ArrowUp,
  ArrowDown,
  MapPin,
  Loader2
} from 'lucide-react';

// Stats Card Component
function StatCard({ icon: Icon, label, value, color = 'dive-neon' }) {
  return (
    <div className="bg-dive-dark border border-dive-gray rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 bg-${color}/20 rounded-lg flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}`} />
        </div>
        <div>
          <p className="text-gray-400 text-sm">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

// Overview Tab
function Overview() {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch basic counts
        const [usersRes, barsRes, ratingsRes, commentsRes, requestsRes] = await Promise.all([
          supabase.from('profiles').select('id', { count: 'exact', head: true }),
          supabase.from('bars').select('id', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('ratings').select('id', { count: 'exact', head: true }),
          supabase.from('comments').select('id', { count: 'exact', head: true }).eq('is_visible', true),
          supabase.from('bar_requests').select('id', { count: 'exact', head: true }).eq('status', 'pending')
        ]);

        setStats({
          totalUsers: usersRes.count || 0,
          totalBars: barsRes.count || 0,
          totalRatings: ratingsRes.count || 0,
          totalComments: commentsRes.count || 0,
          pendingRequests: requestsRes.count || 0
        });

        // Fetch recent activity
        const { data: activity } = await supabase
          .from('activity_log')
          .select(`
            *,
            profiles:user_id (username, display_name)
          `)
          .order('created_at', { ascending: false })
          .limit(10);

        setRecentActivity(activity || []);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <div className="text-gray-400 text-center py-8">Loading stats...</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-display text-white tracking-wide">Overview</h2>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard icon={Users} label="Total Users" value={stats?.totalUsers} color="dive-blue" />
        <StatCard icon={Beer} label="Active Bars" value={stats?.totalBars} color="dive-amber" />
        <StatCard icon={TrendingUp} label="Total Ratings" value={stats?.totalRatings} color="dive-green" />
        <StatCard icon={MessageCircle} label="Comments" value={stats?.totalComments} color="dive-neon" />
        <StatCard icon={ClipboardList} label="Pending" value={stats?.pendingRequests} color="dive-amber" />
      </div>

      {/* Recent Activity */}
      <div>
        <h3 className="text-white font-medium mb-3 flex items-center gap-2">
          <Activity className="w-5 h-5 text-dive-neon" />
          Recent Activity
        </h3>
        <div className="bg-dive-dark border border-dive-gray rounded-xl overflow-hidden">
          {recentActivity.length === 0 ? (
            <p className="text-gray-500 text-center py-6">No recent activity</p>
          ) : (
            <div className="divide-y divide-dive-gray">
              {recentActivity.map(item => (
                <div key={item.id} className="p-3 flex items-center gap-3">
                  <div className="w-8 h-8 bg-dive-neon/20 rounded-full flex items-center justify-center">
                    <span className="text-dive-neon text-xs">
                      {item.profiles?.display_name?.charAt(0) || '?'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <span className="text-white text-sm">
                      {item.profiles?.display_name || 'Unknown'}
                    </span>
                    <span className="text-gray-400 text-sm"> {item.action}</span>
                  </div>
                  <span className="text-gray-500 text-xs">
                    {new Date(item.created_at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// User Analytics Tab
function UserAnalytics() {
  const [metrics, setMetrics] = useState(null);
  const [activityByHour, setActivityByHour] = useState([]);
  const [activityByDay, setActivityByDay] = useState([]);
  const [uniqueUsersByHour, setUniqueUsersByHour] = useState([]);
  const [uniqueUsersByDay, setUniqueUsersByDay] = useState([]);
  const [totalActivityStats, setTotalActivityStats] = useState([]);
  const [uniqueUserStats, setUniqueUserStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityView, setActivityView] = useState('total'); // 'total' or 'unique'
  const [timePeriod, setTimePeriod] = useState('week'); // 'week', 'month', '3months', '6months', 'year', 'ytd'
  const [visitorCount, setVisitorCount] = useState(0);

  const getTimePeriodDays = () => {
    switch (timePeriod) {
      case 'week': return 7;
      case 'month': return 30;
      case '3months': return 90;
      case '6months': return 180;
      case 'year': return 365;
      case 'ytd': {
        const now = new Date();
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        return Math.ceil((now - startOfYear) / (1000 * 60 * 60 * 24));
      }
      default: return 7;
    }
  };

  const timePeriodLabel = {
    'week': 'This Week',
    'month': 'This Month',
    '3months': 'Last 3 Months',
    '6months': 'Last 6 Months',
    'year': 'Last Year',
    'ytd': 'Year to Date'
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timePeriod]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Use RPC functions for DISTINCT user counts (if available)
      // Fall back to JS-based counting if functions don't exist
      let dau = 0, wau = 0, mau = 0, yau = 0;
      let prevDau = 0, prevWau = 0, prevMau = 0;

      try {
        // Try using database functions for accurate counts
        const [dauRes, wauRes, mauRes, yauRes, prevDauRes, prevWauRes, prevMauRes] = await Promise.all([
          supabase.rpc('get_dau'),
          supabase.rpc('get_wau'),
          supabase.rpc('get_mau'),
          supabase.rpc('get_yau'),
          supabase.rpc('get_prev_dau'),
          supabase.rpc('get_prev_wau'),
          supabase.rpc('get_prev_mau')
        ]);
        
        dau = dauRes.data || 0;
        wau = wauRes.data || 0;
        mau = mauRes.data || 0;
        yau = yauRes.data || 0;
        prevDau = prevDauRes.data || 0;
        prevWau = prevWauRes.data || 0;
        prevMau = prevMauRes.data || 0;
      } catch (rpcError) {
        console.warn('RPC functions not available, falling back to JS counting:', rpcError);
        
        // Fallback: Fetch all login events and count distinct users in JS
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        const oneYearAgo = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000);

        const { data: loginData } = await supabase
          .from('activity_log')
          .select('user_id, created_at')
          .eq('action', 'login')
          .gte('created_at', oneYearAgo.toISOString());

        const uniqueToday = new Set();
        const uniqueWeek = new Set();
        const uniqueMonth = new Set();
        const uniqueYear = new Set();

        (loginData || []).forEach(item => {
          const date = new Date(item.created_at);
          uniqueYear.add(item.user_id);
          if (date >= oneMonthAgo) uniqueMonth.add(item.user_id);
          if (date >= oneWeekAgo) uniqueWeek.add(item.user_id);
          if (date >= today) uniqueToday.add(item.user_id);
        });

        dau = uniqueToday.size;
        wau = uniqueWeek.size;
        mau = uniqueMonth.size;
        yau = uniqueYear.size;
      }

      // Calculate percentage changes
      const calcChange = (current, previous) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return Math.round(((current - previous) / previous) * 100);
      };

      setMetrics({
        dau: { count: dau, change: calcChange(dau, prevDau) },
        wau: { count: wau, change: calcChange(wau, prevWau) },
        mau: { count: mau, change: calcChange(mau, prevMau) },
        yau: { count: yau, change: 0 }
      });

      // Calculate time period for charts
      const periodDays = getTimePeriodDays();
      const periodStart = new Date(Date.now() - periodDays * 24 * 60 * 60 * 1000);
      
      const { data: hourlyData } = await supabase
        .from('activity_log')
        .select('created_at, user_id')
        .eq('action', 'login')
        .gte('created_at', periodStart.toISOString())
        .limit(10000); // Increase from default 1000

      // Process hourly data - TOTAL logins (convert to Chicago time)
      const hourCounts = Array(24).fill(0);
      const uniqueUsersByHourMap = Array(24).fill(null).map(() => new Set());
      
      (hourlyData || []).forEach(item => {
        const date = new Date(item.created_at);
        const chicagoHour = (date.getUTCHours() - 6 + 24) % 24;
        hourCounts[chicagoHour]++;
        uniqueUsersByHourMap[chicagoHour].add(item.user_id);
      });
      
      setActivityByHour(hourCounts.map((count, hour) => ({
        hour: `${hour % 12 === 0 ? 12 : hour % 12}${hour < 12 ? 'am' : 'pm'}`,
        count
      })));
      
      setUniqueUsersByHour(uniqueUsersByHourMap.map((users, hour) => ({
        hour: `${hour % 12 === 0 ? 12 : hour % 12}${hour < 12 ? 'am' : 'pm'}`,
        count: users.size
      })));

      // Process day of week data - TOTAL and UNIQUE
      const dayCounts = Array(7).fill(0);
      const uniqueUsersByDayMap = Array(7).fill(null).map(() => new Set());
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      
      (hourlyData || []).forEach(item => {
        const date = new Date(item.created_at);
        dayCounts[date.getDay()]++;
        uniqueUsersByDayMap[date.getDay()].add(item.user_id);
      });
      
      setActivityByDay(dayNames.map((name, i) => ({
        day: name,
        count: dayCounts[i]
      })));
      
      setUniqueUsersByDay(dayNames.map((name, i) => ({
        day: name,
        count: uniqueUsersByDayMap[i].size
      })));

      // Fetch ALL activity for breakdown (using selected time period)
      const { data: actionsData } = await supabase
        .from('activity_log')
        .select('action, user_id')
        .gte('created_at', periodStart.toISOString())
        .limit(10000); // Increase from default 1000
      
      // Track unique page visits (visitors without login)
      const { count: visitCount } = await supabase
        .from('page_visits')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', periodStart.toISOString());
      setVisitorCount(visitCount || 0);

      // Calculate TOTAL events per action
      const totalCounts = {};
      (actionsData || []).forEach(item => {
        totalCounts[item.action] = (totalCounts[item.action] || 0) + 1;
      });
      setTotalActivityStats(Object.entries(totalCounts).map(([action, count]) => ({
        action: action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        count
      })).sort((a, b) => b.count - a.count));

      // Calculate UNIQUE users per action
      const uniqueUsersByAction = {};
      (actionsData || []).forEach(item => {
        if (!uniqueUsersByAction[item.action]) {
          uniqueUsersByAction[item.action] = new Set();
        }
        uniqueUsersByAction[item.action].add(item.user_id);
      });
      setUniqueUserStats(Object.entries(uniqueUsersByAction).map(([action, users]) => ({
        action: action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        count: users.size
      })).sort((a, b) => b.count - a.count));

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-dive-neon border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const maxHourCount = Math.max(...activityByHour.map(h => h.count), 1);
  const maxDayCount = Math.max(...activityByDay.map(d => d.count), 1);
  const maxUniqueHourCount = Math.max(...uniqueUsersByHour.map(h => h.count), 1);
  const maxUniqueDayCount = Math.max(...uniqueUsersByDay.map(d => d.count), 1);

  return (
    <div className="space-y-6">
      {/* Active Users Metrics */}
      <div>
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-dive-neon" />
          Active Users
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Daily Active', key: 'dau', color: 'dive-neon' },
            { label: 'Weekly Active', key: 'wau', color: 'dive-amber' },
            { label: 'Monthly Active', key: 'mau', color: 'dive-blue' },
            { label: 'Yearly Active', key: 'yau', color: 'purple-500' }
          ].map(metric => (
            <div key={metric.key} className="bg-dive-dark border border-dive-gray rounded-xl p-4">
              <p className="text-gray-400 text-xs mb-1">{metric.label}</p>
              <p className={`text-2xl font-bold text-${metric.color}`}>
                {metrics?.[metric.key]?.count || 0}
              </p>
              {metrics?.[metric.key]?.change !== 0 && metric.key !== 'yau' && (
                <div className={`flex items-center gap-1 mt-1 text-xs ${
                  metrics[metric.key].change > 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {metrics[metric.key].change > 0 ? (
                    <ArrowUp className="w-3 h-3" />
                  ) : (
                    <ArrowDown className="w-3 h-3" />
                  )}
                  <span>{Math.abs(metrics[metric.key].change)}%</span>
                  <span className="text-gray-500">vs previous</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Time Period Filter */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-gray-400 text-sm">Time Period:</span>
        {Object.entries(timePeriodLabel).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTimePeriod(key)}
            className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
              timePeriod === key
                ? 'bg-dive-neon text-white'
                : 'bg-dive-dark text-gray-400 hover:text-white border border-dive-gray'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Unique Page Visitors */}
      {visitorCount > 0 && (
        <div className="bg-dive-dark border border-dive-gray rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-dive-green" />
              <span className="text-gray-400">Unique Page Visits ({timePeriodLabel[timePeriod]})</span>
            </div>
            <span className="text-2xl font-bold text-dive-green">{visitorCount.toLocaleString()}</span>
          </div>
        </div>
      )}

      {/* Unique Users Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Unique Users by Hour */}
        <div>
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-dive-green" />
            Unique Users by Hour (CT) - {timePeriodLabel[timePeriod]}
          </h3>
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-4">
            <div className="flex">
              {/* Y-axis labels */}
              <div className="flex flex-col justify-between text-xs text-gray-500 pr-2 w-8 text-right" style={{ height: '120px' }}>
                <span>{maxUniqueHourCount}</span>
                <span>{Math.round(maxUniqueHourCount / 2)}</span>
                <span>0</span>
              </div>
              {/* Chart area */}
              <div className="flex-1 border-l border-b border-dive-gray">
                <div className="flex h-[120px]">
                  {uniqueUsersByHour.map((hour, i) => {
                    const heightPercent = maxUniqueHourCount > 0 ? (hour.count / maxUniqueHourCount) * 100 : 0;
                    return (
                      <div key={i} className="flex-1 flex items-end justify-center relative group px-px">
                        <div
                          className="w-full bg-dive-green rounded-t cursor-pointer hover:bg-dive-green/80 transition-colors"
                          style={{ height: `${heightPercent}%`, minHeight: hour.count > 0 ? '3px' : '0' }}
                        />
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-dive-darker border border-dive-gray px-2 py-1 rounded text-xs text-white whitespace-nowrap z-20 shadow-lg">
                          {hour.hour}: {hour.count} users
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500 ml-8">
              <span>12am</span>
              <span>6am</span>
              <span>12pm</span>
              <span>6pm</span>
              <span>12am</span>
            </div>
            <div className="text-center text-xs text-gray-500 mt-2">
              Peak: {Math.max(...uniqueUsersByHour.map(h => h.count))} unique users
            </div>
          </div>
        </div>

        {/* Unique Users by Day */}
        <div>
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            Unique Users by Day - {timePeriodLabel[timePeriod]}
          </h3>
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-4">
            <div className="flex">
              {/* Y-axis labels */}
              <div className="flex flex-col justify-between text-xs text-gray-500 pr-2 w-8 text-right" style={{ height: '120px' }}>
                <span>{maxUniqueDayCount}</span>
                <span>{Math.round(maxUniqueDayCount / 2)}</span>
                <span>0</span>
              </div>
              {/* Chart area */}
              <div className="flex-1 border-l border-b border-dive-gray">
                <div className="flex h-[120px] gap-1">
                  {uniqueUsersByDay.map((day, i) => {
                    const heightPercent = maxUniqueDayCount > 0 ? (day.count / maxUniqueDayCount) * 100 : 0;
                    return (
                      <div key={i} className="flex-1 flex items-end justify-center relative group">
                        <div
                          className="w-full bg-purple-500 rounded-t cursor-pointer hover:bg-purple-400 transition-colors"
                          style={{ height: `${heightPercent}%`, minHeight: day.count > 0 ? '3px' : '0' }}
                        />
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-dive-darker border border-dive-gray px-2 py-1 rounded text-xs text-white whitespace-nowrap z-20 shadow-lg">
                          {day.day}: {day.count} users
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-around mt-2 ml-8">
              {uniqueUsersByDay.map((day, i) => (
                <span key={i} className="text-xs text-gray-400">{day.day}</span>
              ))}
            </div>
            <div className="text-center text-xs text-gray-500 mt-2">
              Peak: {Math.max(...uniqueUsersByDay.map(d => d.count))} unique users
            </div>
          </div>
        </div>
      </div>

      {/* Total Logins Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Total Logins by Hour */}
        <div>
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-dive-amber" />
            Total Logins by Hour (CT) - {timePeriodLabel[timePeriod]}
          </h3>
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-4">
            <div className="flex">
              {/* Y-axis labels */}
              <div className="flex flex-col justify-between text-xs text-gray-500 pr-2 w-8 text-right" style={{ height: '120px' }}>
                <span>{maxHourCount}</span>
                <span>{Math.round(maxHourCount / 2)}</span>
                <span>0</span>
              </div>
              {/* Chart area */}
              <div className="flex-1 border-l border-b border-dive-gray">
                <div className="flex h-[120px]">
                  {activityByHour.map((hour, i) => {
                    const heightPercent = maxHourCount > 0 ? (hour.count / maxHourCount) * 100 : 0;
                    return (
                      <div key={i} className="flex-1 flex items-end justify-center relative group px-px">
                        <div
                          className="w-full bg-dive-amber rounded-t cursor-pointer hover:bg-dive-amber/80 transition-colors"
                          style={{ height: `${heightPercent}%`, minHeight: hour.count > 0 ? '3px' : '0' }}
                        />
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-dive-darker border border-dive-gray px-2 py-1 rounded text-xs text-white whitespace-nowrap z-20 shadow-lg">
                          {hour.hour}: {hour.count} logins
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500 ml-8">
              <span>12am</span>
              <span>6am</span>
              <span>12pm</span>
              <span>6pm</span>
              <span>12am</span>
            </div>
            <div className="text-center text-xs text-gray-500 mt-2">
              Total: {activityByHour.reduce((sum, h) => sum + h.count, 0)} logins
            </div>
          </div>
        </div>

        {/* Total Logins by Day */}
        <div>
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-dive-blue" />
            Total Logins by Day - {timePeriodLabel[timePeriod]}
          </h3>
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-4">
            <div className="flex">
              {/* Y-axis labels */}
              <div className="flex flex-col justify-between text-xs text-gray-500 pr-2 w-8 text-right" style={{ height: '120px' }}>
                <span>{maxDayCount}</span>
                <span>{Math.round(maxDayCount / 2)}</span>
                <span>0</span>
              </div>
              {/* Chart area */}
              <div className="flex-1 border-l border-b border-dive-gray">
                <div className="flex h-[120px] gap-1">
                  {activityByDay.map((day, i) => {
                    const heightPercent = maxDayCount > 0 ? (day.count / maxDayCount) * 100 : 0;
                    return (
                      <div key={i} className="flex-1 flex items-end justify-center relative group">
                        <div
                          className="w-full bg-dive-blue rounded-t cursor-pointer hover:bg-dive-blue/80 transition-colors"
                          style={{ height: `${heightPercent}%`, minHeight: day.count > 0 ? '3px' : '0' }}
                        />
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-dive-darker border border-dive-gray px-2 py-1 rounded text-xs text-white whitespace-nowrap z-20 shadow-lg">
                          {day.day}: {day.count} logins
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-around mt-2 ml-8">
              {activityByDay.map((day, i) => (
                <span key={i} className="text-xs text-gray-400">{day.day}</span>
              ))}
            </div>
            <div className="text-center text-xs text-gray-500 mt-2">
              Total: {activityByDay.reduce((sum, d) => sum + d.count, 0)} logins
            </div>
          </div>
        </div>
      </div>

      {/* Action Breakdown - Two Views */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-400" />
            Activity Breakdown ({timePeriodLabel[timePeriod]})
          </h3>
          <div className="flex rounded-lg overflow-hidden border border-dive-gray">
            <button
              onClick={() => setActivityView('total')}
              className={`px-3 py-1.5 text-sm transition-colors ${
                activityView === 'total'
                  ? 'bg-dive-neon text-white'
                  : 'bg-dive-dark text-gray-400 hover:text-white'
              }`}
            >
              Total Activity
            </button>
            <button
              onClick={() => setActivityView('unique')}
              className={`px-3 py-1.5 text-sm transition-colors ${
                activityView === 'unique'
                  ? 'bg-dive-neon text-white'
                  : 'bg-dive-dark text-gray-400 hover:text-white'
              }`}
            >
              Unique Users
            </button>
          </div>
        </div>
        
        <div className="bg-dive-dark border border-dive-gray rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-dive-darker border-b border-dive-gray">
            <p className="text-gray-400 text-xs">
              {activityView === 'total' 
                ? 'Shows total number of events (same user can be counted multiple times)'
                : 'Shows unique users who performed each action at least once'
              }
            </p>
          </div>
          {(activityView === 'total' ? totalActivityStats : uniqueUserStats).length === 0 ? (
            <p className="text-gray-500 text-center py-6">No activity data</p>
          ) : (
            <div className="divide-y divide-dive-gray">
              {(activityView === 'total' ? totalActivityStats : uniqueUserStats).map(({ action, count }) => (
                <div key={action} className="p-3 flex items-center justify-between">
                  <span className="text-white text-sm">{action}</span>
                  <span className={`font-medium ${activityView === 'total' ? 'text-dive-neon' : 'text-dive-amber'}`}>
                    {count} {activityView === 'unique' && <span className="text-gray-500 text-xs">users</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Refresh Button */}
      <button
        onClick={fetchAnalytics}
        className="btn-secondary flex items-center gap-2"
      >
        <RefreshCw className="w-4 h-4" />
        Refresh Data
      </button>
    </div>
  );
}

// Users Management Tab
function UsersManagement() {
  const { profile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [banningUser, setBanningUser] = useState(null);
  const [banReason, setBanReason] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Fetch users with their rating and comment counts
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get rating counts
      const { data: ratingCounts } = await supabase
        .from('ratings')
        .select('user_id');

      // Get comment counts
      const { data: commentCounts } = await supabase
        .from('comments')
        .select('user_id');

      // Merge counts with users
      const usersWithCounts = (profiles || []).map(user => ({
        ...user,
        ratingCount: (ratingCounts || []).filter(r => r.user_id === user.id).length,
        commentCount: (commentCounts || []).filter(c => c.user_id === user.id).length
      }));

      if (search) {
        setUsers(usersWithCounts.filter(u => 
          u.username?.toLowerCase().includes(search.toLowerCase()) ||
          u.display_name?.toLowerCase().includes(search.toLowerCase())
        ));
      } else {
        setUsers(usersWithCounts);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [search]);

  const toggleAdmin = async (userId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_admin: !currentStatus })
        .eq('id', userId);

      if (error) throw error;
      fetchUsers();
    } catch (error) {
      console.error('Error updating admin status:', error);
      alert('Failed to update admin status');
    }
  };

  const toggleBan = async (user) => {
    const isBanning = !user.is_banned;
    try {
      console.log('Banning/unbanning user:', user.id, 'isBanning:', isBanning);
      
      // Update profile ban status
      const { data, error } = await supabase
        .from('profiles')
        .update({ 
          is_banned: isBanning,
          ban_reason: isBanning ? (banReason || 'Violated community guidelines') : null,
          banned_at: isBanning ? new Date().toISOString() : null
        })
        .eq('id', user.id)
        .select();

      if (error) {
        console.error('Error updating profile:', error);
        if (error.message.includes('row-level security')) {
          alert('Permission denied: Make sure you have admin privileges and run the fix_admin_ban_delete.sql migration.');
        }
        throw error;
      }
      
      console.log('Profile update result:', data);
      
      // Verify the update worked by checking if data was returned
      if (!data || data.length === 0) {
        console.warn('Profile update returned no data - this may indicate an RLS issue');
      }
      
      // If banning, also add to banned_list to prevent re-signup
      if (isBanning && user.username) {
        // Use username-based email placeholder since profiles table doesn't have email
        const { error: banListError } = await supabase
          .from('banned_list')
          .upsert({
            email: user.email || `${user.username}@banned.placeholder`, 
            username: user.username,
            banned_by: profile?.username || 'admin',
            reason: banReason || 'Violated community guidelines'
          }, { onConflict: 'email' });
        
        if (banListError) {
          console.error('Error adding to banned_list:', banListError);
          // Show warning but don't fail - the profile ban itself may have succeeded
          if (banListError.message.includes('row-level security')) {
            console.warn('Run fix_admin_ban_delete.sql to enable admin access to banned_list');
          }
        }
      } else if (!isBanning && user.username) {
        // If unbanning, remove from banned_list
        const { error: unbanError } = await supabase
          .from('banned_list')
          .delete()
          .eq('username', user.username);
        
        if (unbanError) {
          console.warn('Error removing from banned_list:', unbanError);
        }
      }
      
      alert(isBanning ? `${user.display_name} has been banned.` : `${user.display_name} has been unbanned.`);
      setBanningUser(null);
      setBanReason('');
      
      // Small delay before refresh to ensure database has updated
      setTimeout(() => fetchUsers(), 500);
    } catch (error) {
      console.error('Error updating ban status:', error);
      alert('Failed to update ban status: ' + error.message);
    }
  };

  const deleteUserAccount = async (user) => {
    if (!confirm(`Are you sure you want to PERMANENTLY DELETE ${user.display_name}'s account? This cannot be undone.`)) {
      return;
    }
    
    try {
      console.log('Starting deletion of user:', user.id);
      
      // Delete user's data in order (respecting foreign keys)
      // Using maybeSingle() and handling errors gracefully
      
      // 1. Delete friend rating comments
      const { error: e1 } = await supabase.from('friend_rating_comments').delete().eq('user_id', user.id);
      if (e1) console.log('friend_rating_comments delete:', e1.message);
      
      // 2. Delete notifications
      const { error: e2 } = await supabase.from('notifications').delete().eq('user_id', user.id);
      if (e2) console.log('notifications (user_id) delete:', e2.message);
      const { error: e2b } = await supabase.from('notifications').delete().eq('from_user_id', user.id);
      if (e2b) console.log('notifications (from_user_id) delete:', e2b.message);
      
      // 3. Delete friend requests
      const { error: e3 } = await supabase.from('friend_requests').delete().eq('from_user_id', user.id);
      if (e3) console.log('friend_requests (from) delete:', e3.message);
      const { error: e3b } = await supabase.from('friend_requests').delete().eq('to_user_id', user.id);
      if (e3b) console.log('friend_requests (to) delete:', e3b.message);
      
      // 4. Delete friendships
      const { error: e4 } = await supabase.from('friendships').delete().eq('user_id', user.id);
      if (e4) console.log('friendships (user_id) delete:', e4.message);
      const { error: e4b } = await supabase.from('friendships').delete().eq('friend_id', user.id);
      if (e4b) console.log('friendships (friend_id) delete:', e4b.message);
      
      // 4c. Delete follows
      const { error: e4c } = await supabase.from('follows').delete().eq('follower_id', user.id);
      if (e4c) console.log('follows (follower_id) delete:', e4c.message);
      const { error: e4d } = await supabase.from('follows').delete().eq('following_id', user.id);
      if (e4d) console.log('follows (following_id) delete:', e4d.message);
      
      // 5. Delete list bars
      const { data: userLists } = await supabase.from('lists').select('id').eq('user_id', user.id);
      if (userLists) {
        for (const list of userLists) {
          await supabase.from('list_bars').delete().eq('list_id', list.id);
        }
      }
      
      // 6. Delete lists
      const { error: e6 } = await supabase.from('lists').delete().eq('user_id', user.id);
      if (e6) console.log('lists delete:', e6.message);
      
      // 7. Delete bar photos
      const { error: e7 } = await supabase.from('bar_photos').delete().eq('user_id', user.id);
      if (e7) console.log('bar_photos delete:', e7.message);
      
      // 8. Delete feature votes
      const { error: e8v } = await supabase.from('feature_votes').delete().eq('user_id', user.id);
      if (e8v) console.log('feature_votes delete:', e8v.message);
      
      // 9. Delete comments
      const { error: e8 } = await supabase.from('comments').delete().eq('user_id', user.id);
      if (e8) console.log('comments delete:', e8.message);
      
      // 10. Delete ratings
      const { error: e9 } = await supabase.from('ratings').delete().eq('user_id', user.id);
      if (e9) console.log('ratings delete:', e9.message);
      
      // 11. Delete activity log
      const { error: e10 } = await supabase.from('activity_log').delete().eq('user_id', user.id);
      if (e10) console.log('activity_log delete:', e10.message);
      
      // 12. Delete profile (CRITICAL)
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);
      
      if (profileError) {
        console.error('Error deleting profile:', profileError);
        throw new Error(`Failed to delete profile: ${profileError.message}`);
      }
      
      console.log('Profile deleted successfully');
      
      // Delete the auth user via server-side RPC (requires SECURITY DEFINER)
      const { error: authDeleteError } = await supabase.rpc('admin_delete_user', {
        target_user_id: user.id
      });
      if (authDeleteError) {
        console.warn('Could not delete auth user (may need SQL editor):', authDeleteError.message);
      } else {
        console.log('Auth user deleted successfully');
      }
      
      alert(`${user.display_name}'s account has been fully deleted.`);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user: ' + error.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-display text-white tracking-wide">Users</h2>
        <button onClick={fetchUsers} className="p-2 text-gray-400 hover:text-white">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10"
        />
      </div>

      {loading ? (
        <p className="text-gray-400 text-center py-8">Loading...</p>
      ) : (
        <div className="bg-dive-dark border border-dive-gray rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-dive-darker text-gray-400 text-sm">
              <tr>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Username</th>
                <th className="text-center p-3">Ratings</th>
                <th className="text-center p-3">Comments</th>
                <th className="text-left p-3">Joined</th>
                <th className="text-center p-3">Status</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dive-gray">
              {users.map(user => (
                <tr key={user.id} className={`text-sm ${user.is_banned ? 'bg-red-500/5' : ''}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        user.is_banned ? 'bg-red-500/20' : 'bg-dive-neon/20'
                      }`}>
                        <span className={user.is_banned ? 'text-red-400 text-sm' : 'text-dive-neon text-sm'}>
                          {user.display_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className={user.is_banned ? 'text-gray-500' : 'text-white'}>
                        {user.display_name}
                      </span>
                    </div>
                  </td>
                  <td className="p-3 text-gray-400">@{user.username}</td>
                  <td className="p-3 text-center text-gray-400">{user.ratingCount}</td>
                  <td className="p-3 text-center text-gray-400">{user.commentCount}</td>
                  <td className="p-3 text-gray-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-3 text-center">
                    {user.is_banned ? (
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                        Banned
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => toggleAdmin(user.id, user.is_admin)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          user.is_admin
                            ? 'bg-dive-neon/20 text-dive-neon'
                            : 'bg-dive-gray text-gray-500 hover:text-white'
                        }`}
                        title={user.is_admin ? 'Remove admin' : 'Make admin'}
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => user.is_banned ? toggleBan(user) : setBanningUser(user)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          user.is_banned
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                            : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        }`}
                        title={user.is_banned ? 'Unban user' : 'Ban user'}
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteUserAccount(user)}
                        className="p-1.5 rounded-lg bg-gray-500/20 text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                        title="Delete account permanently"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Ban Confirmation Modal */}
      {banningUser && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dive-dark border border-red-500/50 rounded-xl max-w-md w-full p-6">
            <h3 className="text-white text-lg font-medium mb-4">
              Ban {banningUser.display_name}?
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              This will prevent the user from logging in. They will see a message that their account has been disabled.
            </p>
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-1 block">Reason (optional)</label>
              <input
                type="text"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Violation of community guidelines..."
                className="w-full"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setBanningUser(null);
                  setBanReason('');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() => toggleBan(banningUser)}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Ban User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Bar Edit Modal Component
function BarEditModal({ bar, onClose, onSave }) {
  const initialMetro = getMetroById(bar.metro_id || bar.metroId) || inferMetroForRequest(bar);
  const [form, setForm] = useState({
    name: bar.name || '',
    address: bar.address || '',
    metroId: initialMetro.id,
    neighborhood: bar.neighborhood || '',
    description: bar.description || '',
    lat: bar.lat || initialMetro.center.lat,
    lng: bar.lng || initialMetro.center.lng,
    attributes: bar.attributes || {}
  });
  const formMetro = getMetroById(form.metroId) || initialMetro;
  const [featureStats, setFeatureStats] = useState([]);
  const [saving, setSaving] = useState(false);

  const allAttributes = [
    { key: 'poolTable', label: 'Pool Table' },
    { key: 'darts', label: 'Darts' },
    { key: 'cashOnly', label: 'Cash Only' },
    { key: 'jukebox', label: 'Jukebox' },
    { key: 'outdoorSeating', label: 'Patio/Outdoor' },
    { key: 'liveMusic', label: 'Live Music' },
    { key: 'happyHour', label: 'Happy Hour' },
    { key: 'touchTunes', label: 'TouchTunes' },
    { key: 'triviaNights', label: 'Trivia Nights' },
    { key: 'euchre', label: 'Euchre' },
    { key: 'oldStyleSign', label: 'Old Style Sign' },
    { key: 'photoBooth', label: 'Photo Booth' },
    { key: 'popcorn', label: 'Popcorn' }
  ].filter((attribute) => getFeaturesForMetro(form.metroId).some((feature) => feature.key === attribute.key));

  useEffect(() => {
    // Fetch community vote stats for this bar
    const fetchFeatureStats = async () => {
      try {
        const { data, error } = await supabase.rpc('get_bar_feature_stats', { bar_uuid: bar.id });
        if (!error && data) {
          setFeatureStats(data);
        }
      } catch (err) {
        console.warn('Could not fetch feature stats:', err);
      }
    };
    fetchFeatureStats();
  }, [bar.id]);

  const getFeatureVoteInfo = (featureKey) => {
    const stat = featureStats.find(s => s.feature_name === featureKey);
    return stat || null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const [geocoding, setGeocoding] = useState(false);
  
  const handleReGeocode = async () => {
    if (!form.address) {
      alert('Please enter an address first');
      return;
    }
    
    setGeocoding(true);
    try {
      const result = await geocodeBarAddress(form.address, {
        metro: formMetro,
        neighborhood: form.neighborhood,
      });

      if (result) {
        const newLat = result.lat.toFixed(6);
        const newLng = result.lng.toFixed(6);
        setForm(prev => ({ ...prev, lat: newLat, lng: newLng }));
        setTimeout(() => {
          alert(`Found: ${result.display}\n\nNew coordinates:\nLat: ${newLat}\nLng: ${newLng}\n\nClick Save to apply changes.`);
        }, 100);
        return;
      }

      alert(`Could not find this address in the ${formMetro.name} area. Check the city and address, or enter coordinates manually.`);
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Geocoding failed. Please try again or enter coordinates manually.');
    } finally {
      setGeocoding(false);
    }
  };

  const handleAttributeToggle = (key) => {
    setForm(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [key]: !prev.attributes[key]
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const allowedFeatures = new Set(getFeaturesForMetro(formMetro.id).map((feature) => feature.key));
      const attributes = Object.fromEntries(
        Object.entries(form.attributes || {}).filter(([key]) =>
          allowedFeatures.has(key) || !['euchre', 'oldStyleSign'].includes(key)
        )
      );
      const updateData = {
        name: form.name,
        address: form.address,
        neighborhood: form.neighborhood,
        description: form.description,
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
        attributes,
        metro_id: formMetro.id,
        state: formMetro.state,
      };
      if ((bar.metro_id || bar.metroId) !== formMetro.id && !/^Broader /.test(form.neighborhood || '')) {
        updateData.city = formMetro.name;
      }
      
      console.log('Saving bar with data:', updateData);
      console.log('Bar ID:', bar.id);
      
      let { data, error } = await supabase
        .from('bars')
        .update(updateData)
        .eq('id', bar.id)
        .select();

      if (error && /metro_id|city|state/i.test(error.message || '')) {
        delete updateData.metro_id;
        delete updateData.state;
        delete updateData.city;
        ({ data, error } = await supabase
          .from('bars')
          .update(updateData)
          .eq('id', bar.id)
          .select());
      }

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Save successful, returned data:', data);
      alert(`Bar saved!\n\nNew coordinates:\nLat: ${updateData.lat}\nLng: ${updateData.lng}`);
      onSave();
    } catch (error) {
      console.error('Error saving bar:', error);
      alert(`Failed to save bar: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-dive-dark border border-dive-gray rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-dive-dark border-b border-dive-gray p-4 flex items-center justify-between">
          <h3 className="text-xl font-display text-white">Edit Bar</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-1 block">Bar Name</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-1 block">Neighborhood</label>
              <input
                type="text"
                name="neighborhood"
                value={form.neighborhood}
                onChange={handleChange}
                className="w-full"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-1 block">City</label>
              <select
                name="metroId"
                value={form.metroId}
                onChange={handleChange}
                className="w-full"
              >
                {METROS.map((metro) => (
                  <option key={metro.id} value={metro.id}>
                    {metro.name}, {metro.state}{metro.enabled ? '' : ' (hidden)'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-1 block">Address</label>
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                className="w-full"
              />
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-1 block">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              className="w-full resize-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-gray-400 text-sm">Coordinates</label>
              <button
                type="button"
                onClick={handleReGeocode}
                disabled={geocoding}
                className="text-xs bg-blue-500/20 border border-blue-500/50 text-blue-400 px-3 py-1 rounded hover:bg-blue-500/30 disabled:opacity-50"
              >
                {geocoding ? '🔄 Geocoding...' : '📍 Re-geocode from Address'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Latitude</label>
                <input
                  type="number"
                  name="lat"
                  value={form.lat}
                  onChange={handleChange}
                  step="0.0001"
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Longitude</label>
                <input
                  type="number"
                  name="lng"
                  value={form.lng}
                  onChange={handleChange}
                  step="0.0001"
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Attributes */}
          <div>
            <label className="text-white font-medium mb-3 block">Features & Attributes</label>
            <div className="grid grid-cols-2 gap-2">
              {allAttributes.map(attr => {
                const voteInfo = getFeatureVoteInfo(attr.key);
                const isEnabled = form.attributes[attr.key];
                
                return (
                  <button
                    key={attr.key}
                    onClick={() => handleAttributeToggle(attr.key)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      isEnabled
                        ? 'bg-dive-neon/20 border-dive-neon text-white'
                        : 'bg-dive-darker border-dive-gray text-gray-400 hover:border-dive-light'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{attr.label}</span>
                      {isEnabled && <Check className="w-4 h-4 text-dive-neon" />}
                    </div>
                    {voteInfo && (
                      <div className="mt-1 text-xs text-gray-500">
                        {voteInfo.yes_votes}/{voteInfo.total_votes} votes ({voteInfo.yes_percentage}%)
                        {voteInfo.is_community_verified && (
                          <span className="ml-1 text-dive-green">✓ Verified</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Last Updated */}
          {bar.updated_at && (
            <p className="text-gray-500 text-xs">
              Last updated: {new Date(bar.updated_at).toLocaleString()}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-dive-gray">
            <button onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Bars Management Tab
function BarsManagement() {
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBar, setEditingBar] = useState(null);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('all');

  const fetchBars = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bars')
        .select('*')
        .order('name');

      if (error) throw error;
      setBars(data || []);
    } catch (error) {
      console.error('Error fetching bars:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBars();
  }, []);

  const barMetroId = (bar) => bar.metro_id || bar.metroId || 'chicago';
  const cityCounts = bars.reduce((counts, bar) => {
    const id = barMetroId(bar);
    counts[id] = (counts[id] || 0) + 1;
    return counts;
  }, {});
  const cityOptions = METROS.filter((metro) => cityCounts[metro.id]);
  const selectedCity = getMetroById(cityFilter);

  const filteredBars = bars.filter(bar =>
    (cityFilter === 'all' || barMetroId(bar) === cityFilter) && (
      bar.name.toLowerCase().includes(search.toLowerCase()) ||
      bar.neighborhood?.toLowerCase().includes(search.toLowerCase())
    )
  );

  const toggleBarActive = async (barId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('bars')
        .update({ is_active: !currentStatus })
        .eq('id', barId);

      if (error) throw error;
      fetchBars();
    } catch (error) {
      console.error('Error updating bar:', error);
    }
  };

  const deleteBar = async (barId) => {
    if (!confirm('Are you sure you want to delete this bar? This will also delete all ratings, comments, and photos for this bar. This cannot be undone.')) return;

    try {
      // Delete related data first (foreign key constraints)
      
      // 1. Delete list_bars entries
      await supabase.from('list_bars').delete().eq('bar_id', barId);
      
      // 2. Delete bar photos
      await supabase.from('bar_photos').delete().eq('bar_id', barId);
      
      // 3. Delete comments
      await supabase.from('comments').delete().eq('bar_id', barId);
      
      // 4. Delete ratings
      await supabase.from('ratings').delete().eq('bar_id', barId);
      
      // 5. Delete qr_scans
      await supabase.from('qr_scans').delete().eq('bar_id', barId);
      
      // 6. Finally delete the bar
      const { error } = await supabase
        .from('bars')
        .delete()
        .eq('id', barId);

      if (error) throw error;
      
      alert('Bar deleted successfully');
      fetchBars();
    } catch (error) {
      console.error('Error deleting bar:', error);
      alert(`Error deleting bar: ${error.message}`);
    }
  };

  // Bulk re-geocode all bars
  const [bulkGeocoding, setBulkGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState({ current: 0, total: 0, results: [] });

  const geocodeAddress = (bar) => geocodeBarAddress(bar.address, {
    metro: getMetroById(bar.metro_id || bar.metroId) || inferMetroForRequest(bar),
    neighborhood: bar.neighborhood,
    delayMs: 1100, // Nominatim rate limit: 1 req/sec
  });

  const bulkReGeocode = async () => {
    const targetBars = selectedCity ? bars.filter((bar) => barMetroId(bar) === selectedCity.id) : bars;
    const scope = selectedCity ? `all ${targetBars.length} ${selectedCity.name} bars` : `ALL ${targetBars.length} bars`;
    if (!confirm(`This will re-geocode ${scope}. This may take several minutes due to rate limits (1 request per second). Continue?`)) return;

    setBulkGeocoding(true);
    const results = [];
    setGeocodeProgress({ current: 0, total: targetBars.length, results: [] });

    for (let i = 0; i < targetBars.length; i++) {
      const bar = targetBars[i];
      setGeocodeProgress(prev => ({ ...prev, current: i + 1 }));

      const result = await geocodeAddress(bar);
      
      if (result) {
        // Update the bar in database
        const { error } = await supabase
          .from('bars')
          .update({ lat: result.lat, lng: result.lng })
          .eq('id', bar.id);

        results.push({
          name: bar.name,
          success: !error,
          newLat: result.lat,
          newLng: result.lng,
          display: result.display
        });
      } else {
        results.push({
          name: bar.name,
          success: false,
          error: 'Could not geocode'
        });
      }

      setGeocodeProgress(prev => ({ ...prev, results: [...results] }));
    }

    setBulkGeocoding(false);
    fetchBars();
    alert(`Geocoding complete!\n\nSuccessful: ${results.filter(r => r.success).length}\nFailed: ${results.filter(r => !r.success).length}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-display text-white tracking-wide">
          Bars ({cityFilter === 'all' ? bars.length : `${filteredBars.length} of ${bars.length}`})
        </h2>
        <div className="flex items-center gap-2">
          <button 
            onClick={bulkReGeocode}
            disabled={bulkGeocoding}
            className="text-xs bg-blue-500/20 border border-blue-500/50 text-blue-400 px-3 py-2 rounded hover:bg-blue-500/30 disabled:opacity-50 flex items-center gap-2"
          >
            <MapPin className="w-4 h-4" />
            {bulkGeocoding
              ? `Geocoding ${geocodeProgress.current}/${geocodeProgress.total}...`
              : `Re-geocode ${selectedCity ? `${selectedCity.name} Bars` : 'All Bars'}`}
          </button>
          <button onClick={fetchBars} className="p-2 text-gray-400 hover:text-white">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Geocoding Progress */}
      {bulkGeocoding && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
            <span className="text-blue-400 text-sm">
              Processing {geocodeProgress.current} of {geocodeProgress.total} bars...
            </span>
          </div>
          <div className="w-full bg-dive-gray rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all"
              style={{ width: `${(geocodeProgress.current / geocodeProgress.total) * 100}%` }}
            />
          </div>
          {geocodeProgress.results.length > 0 && (
            <div className="mt-3 max-h-32 overflow-y-auto text-xs">
              {geocodeProgress.results.slice(-5).map((r, i) => (
                <div key={i} className={r.success ? 'text-green-400' : 'text-red-400'}>
                  {r.success ? '✓' : '✗'} {r.name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search + City */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search bars by name or neighborhood..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10"
          />
        </div>
        <select
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          className="sm:w-56"
          aria-label="Filter by city"
        >
          <option value="all">All cities ({bars.length})</option>
          {cityOptions.map((metro) => (
            <option key={metro.id} value={metro.id}>
              {metro.name}, {metro.state} ({cityCounts[metro.id]})
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-gray-400 text-center py-8">Loading...</p>
      ) : (
        <div className="space-y-3">
          {filteredBars.map(bar => (
            <div
              key={bar.id}
              className={`bg-dive-dark border rounded-xl p-4 ${
                bar.is_active ? 'border-dive-gray' : 'border-red-500/50 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-white font-medium">{bar.name}</h3>
                  <p className="text-gray-400 text-sm">
                    {cityFilter === 'all' && `${getMetroById(barMetroId(bar))?.name || barMetroId(bar)} • `}
                    {bar.neighborhood} • {bar.address}
                  </p>
                  {bar.updated_at && (
                    <p className="text-gray-500 text-xs mt-1">
                      Updated: {new Date(bar.updated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingBar(bar)}
                    className="p-2 text-dive-amber hover:bg-dive-amber/20 rounded-lg"
                    title="Edit bar"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <Link
                    to={`/bar/${bar.id}`}
                    className="p-2 text-gray-400 hover:text-white rounded-lg"
                  >
                    <Eye className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => toggleBarActive(bar.id, bar.is_active)}
                    className={`p-2 rounded-lg ${
                      bar.is_active
                        ? 'text-dive-green hover:bg-dive-green/20'
                        : 'text-gray-500 hover:bg-dive-gray'
                    }`}
                    title={bar.is_active ? 'Hide bar' : 'Show bar'}
                  >
                    {bar.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => deleteBar(bar.id)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingBar && (
        <BarEditModal
          bar={editingBar}
          onClose={() => setEditingBar(null)}
          onSave={() => {
            setEditingBar(null);
            fetchBars();
          }}
        />
      )}
    </div>
  );
}

// Comments Moderation Tab
function CommentsModeration() {
  const [comments, setComments] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentReports, setContentReports] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'visible', 'hidden', 'reported', 'content_reported'

  const fetchComments = async () => {
    setLoading(true);
    try {
      if (filter === 'content_reported') {
        // Fetch content reports (photos, feed comments, etc.)
        try {
          const { data, error } = await supabase
            .from('content_reports')
            .select(`
              *,
              reporter:reported_by_user_id (username, display_name)
            `)
            .eq('status', 'pending')
            .order('created_at', { ascending: false });

          if (!error) {
            setContentReports(data || []);
          }
        } catch (_) {
          setContentReports([]);
        }
        setReports([]);
        setComments([]);
      } else if (filter === 'reported') {
        // Fetch reported comments
        const { data, error } = await supabase
          .from('comment_reports')
          .select(`
            *,
            comments:comment_id (
              *,
              profiles:user_id (username, display_name),
              bars:bar_id (name)
            ),
            reporter:reported_by_user_id (username, display_name)
          `)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setReports(data || []);
        setComments([]);
        setContentReports([]);
      } else {
        // Fetch regular comments
        let query = supabase
          .from('comments')
          .select(`
            *,
            profiles:user_id (username, display_name),
            bars:bar_id (name)
          `)
          .order('created_at', { ascending: false });

        if (filter === 'visible') {
          query = query.eq('is_visible', true);
        } else if (filter === 'hidden') {
          query = query.eq('is_visible', false);
        }

        const { data, error } = await query;
        if (error) throw error;
        setComments(data || []);
        setReports([]);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [filter]);

  const toggleVisibility = async (commentId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('comments')
        .update({ is_visible: !currentStatus })
        .eq('id', commentId);

      if (error) throw error;
      fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const deleteComment = async (commentId, reportId = null) => {
    if (!confirm('Delete this comment? This action cannot be undone.')) return;

    try {
      // Delete the comment
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      // If there was a report, mark it as reviewed
      if (reportId) {
        await supabase
          .from('comment_reports')
          .update({ status: 'reviewed' })
          .eq('id', reportId);
      }

      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const dismissReport = async (reportId) => {
    try {
      const { error } = await supabase
        .from('comment_reports')
        .update({ status: 'dismissed' })
        .eq('id', reportId);

      if (error) throw error;
      fetchComments();
    } catch (error) {
      console.error('Error dismissing report:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-display text-white tracking-wide">Comments</h2>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-dive-dark border border-dive-gray rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            <option value="visible">Visible</option>
            <option value="hidden">Hidden</option>
            <option value="reported">Reported Comments</option>
            <option value="content_reported">Content Reports</option>
          </select>
          <button onClick={fetchComments} className="p-2 text-gray-400 hover:text-white">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-400 text-center py-8">Loading...</p>
      ) : filter === 'content_reported' ? (
        // Content Reports View (photos, feed comments, etc.)
        contentReports.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No content reports</p>
        ) : (
          <div className="space-y-3">
            {contentReports.map(report => (
              <div key={report.id} className="bg-dive-dark border border-red-500/50 rounded-xl p-4">
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 mb-3">
                  <div className="flex items-center gap-2 text-red-400 text-xs flex-wrap">
                    <span className="font-medium">Type:</span>
                    <span className="capitalize">{report.content_type?.replace('_', ' ')}</span>
                    <span className="text-gray-500">|</span>
                    <span className="font-medium">Reported by:</span>
                    <span>{report.reporter?.display_name || 'Unknown'}</span>
                    <span className="text-gray-500">|</span>
                    <span className="capitalize">{report.reason?.replace('_', ' ')}</span>
                  </div>
                </div>
                {report.details && (
                  <p className="text-gray-400 text-sm mb-2">{report.details}</p>
                )}
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                  <span>ID: {report.content_id}</span>
                  <span>|</span>
                  <span>{new Date(report.created_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={async () => {
                      await supabase.from('content_reports').update({ status: 'dismissed' }).eq('id', report.id);
                      fetchComments();
                    }}
                    className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg text-xs"
                    title="Dismiss report"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={async () => {
                      await supabase.from('content_reports').update({ status: 'reviewed' }).eq('id', report.id);
                      fetchComments();
                    }}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg text-xs"
                    title="Mark reviewed (content removed)"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : filter === 'reported' ? (
        // Reported Comments View
        reports.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No reported comments</p>
        ) : (
          <div className="space-y-3">
            {reports.map(report => (
              <div
                key={report.id}
                className="bg-dive-dark border border-red-500/50 rounded-xl p-4"
              >
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 mb-3">
                  <div className="flex items-center gap-2 text-red-400 text-xs">
                    <span className="font-medium">Reported by:</span>
                    <span>{report.reporter?.display_name || 'Unknown'}</span>
                    <span className="text-gray-500">•</span>
                    <span className="capitalize">{report.reason.replace('_', ' ')}</span>
                    {report.details && (
                      <>
                        <span className="text-gray-500">•</span>
                        <span className="text-gray-400">{report.details}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-white text-sm font-medium">
                        {report.comments?.profiles?.display_name || 'Unknown'}
                      </span>
                      <span className="text-gray-500 text-xs">on</span>
                      <span className="text-dive-amber text-sm">{report.comments?.bars?.name || 'Unknown'}</span>
                    </div>
                    <p className="text-gray-300 text-sm">{report.comments?.text}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => dismissReport(report.id)}
                      className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg"
                      title="Dismiss report"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteComment(report.comment_id, report.id)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                      title="Delete comment"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : comments.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No comments found</p>
      ) : (
        <div className="space-y-3">
          {comments.map(comment => (
            <div
              key={comment.id}
              className={`bg-dive-dark border rounded-xl p-4 ${
                comment.is_visible ? 'border-dive-gray' : 'border-red-500/50'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-white text-sm font-medium">
                      {comment.profiles?.display_name}
                    </span>
                    <span className="text-gray-500 text-xs">on</span>
                    <span className="text-dive-amber text-sm">{comment.bars?.name}</span>
                    <span className="text-gray-500 text-xs">
                      • {new Date(comment.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className={`text-sm ${comment.is_visible ? 'text-gray-300' : 'text-gray-500 line-through'}`}>
                    {comment.text}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleVisibility(comment.id, comment.is_visible)}
                    className={`p-2 rounded-lg ${
                      comment.is_visible
                        ? 'text-dive-green hover:bg-dive-green/20'
                        : 'text-gray-500 hover:bg-dive-gray'
                    }`}
                  >
                    {comment.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => deleteComment(comment.id)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Bar Requests Tab
function BarRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bar_requests')
        .select(`
          *,
          profiles:submitted_by (username, display_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleApprove = async (request) => {
    try {
      // Get neighborhood coordinates
      const neighborhoodCoords = {
        'Wicker Park': [41.9088, -87.6796],
        'Logan Square': [41.9234, -87.7080],
        'Lincoln Park': [41.9214, -87.6513],
        'Old Town': [41.9109, -87.6387],
        'Pilsen': [41.8565, -87.6564],
        'Bridgeport': [41.8380, -87.6512],
        'Uptown': [41.9663, -87.6542],
        'Andersonville': [41.9797, -87.6683],
        'Rogers Park': [42.0087, -87.6681],
        'Ukrainian Village': [41.8989, -87.6825],
        'Humboldt Park': [41.9020, -87.7199],
        'Bucktown': [41.9217, -87.6796],
        'Lakeview': [41.9435, -87.6534],
        'Albany Park': [41.9681, -87.7230],
        'Avondale': [41.9390, -87.7111]
      };

      const coords = neighborhoodCoords[request.neighborhood] || [41.8781, -87.6298];
      const latOffset = (Math.random() - 0.5) * 0.01;
      const lngOffset = (Math.random() - 0.5) * 0.01;

      // Create the bar
      const { error: barError } = await supabase
        .from('bars')
        .insert({
          name: request.name,
          address: request.address,
          neighborhood: request.neighborhood,
          description: request.description,
          attributes: request.attributes,
          lat: coords[0] + latOffset,
          lng: coords[1] + lngOffset,
          is_active: true,
          created_by: request.submitted_by
        });

      if (barError) throw barError;

      // Update request status
      const { error: updateError } = await supabase
        .from('bar_requests')
        .update({
          status: 'approved',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', request.id);

      if (updateError) throw updateError;

      // Increment the user's bars_approved_count for badge tracking
      if (request.submitted_by) {
        await supabase.rpc('increment_bars_approved_count', { 
          p_user_id: request.submitted_by 
        });
      }

      fetchRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Failed to approve request');
    }
  };

  const handleReject = async (requestId) => {
    if (!confirm('Reject this bar request?')) return;

    try {
      const { error } = await supabase
        .from('bar_requests')
        .update({
          status: 'rejected',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;
      fetchRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-dive-amber/20 text-dive-amber',
      approved: 'bg-dive-green/20 text-dive-green',
      rejected: 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${styles[status]}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-display text-white tracking-wide">Bar Requests</h2>
        <button onClick={fetchRequests} className="p-2 text-gray-400 hover:text-white">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400 text-center py-8">Loading...</p>
      ) : requests.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No bar requests</p>
      ) : (
        <div className="space-y-3">
          {requests.map(request => (
            <div
              key={request.id}
              className="bg-dive-dark border border-dive-gray rounded-xl p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-white font-medium">{request.name}</h3>
                    {getStatusBadge(request.status)}
                  </div>
                  <p className="text-gray-400 text-sm mb-1">
                    {request.neighborhood} {request.address && `• ${request.address}`}
                  </p>
                  {request.description && (
                    <p className="text-gray-500 text-sm mb-2">{request.description}</p>
                  )}
                  <p className="text-gray-500 text-xs">
                    Submitted by {request.profiles?.display_name} on{' '}
                    {new Date(request.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                {request.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleApprove(request)}
                      className="p-2 bg-dive-green/20 text-dive-green hover:bg-dive-green/30 rounded-lg"
                      title="Approve"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleReject(request.id)}
                      className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg"
                      title="Reject"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Main Admin Dashboard Component
function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'bars', label: 'Bars', icon: Beer },
    { id: 'comments', label: 'Comments', icon: MessageCircle },
    { id: 'requests', label: 'Requests', icon: ClipboardList }
  ];

  return (
    <div className="min-h-screen bg-dive-black">
      {/* Header */}
      <div className="bg-dive-darker border-b border-dive-gray" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-dive-neon/20 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-dive-neon" />
                </div>
                <div>
                  <h1 className="font-display text-xl text-white tracking-wide">
                    ADMIN DASHBOARD
                  </h1>
                  <p className="text-gray-500 text-xs">Manage Tilted</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <div className="md:w-48 flex-shrink-0">
            <nav className="flex md:flex-col gap-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    activeTab === tab.id
                      ? 'bg-dive-neon text-white'
                      : 'text-gray-400 hover:bg-dive-dark hover:text-white'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="hidden md:inline">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1">
            {activeTab === 'overview' && <Overview />}
            {activeTab === 'analytics' && <UserAnalytics />}
            {activeTab === 'users' && <UsersManagement />}
            {activeTab === 'bars' && <BarsManagement />}
            {activeTab === 'comments' && <CommentsModeration />}
            {activeTab === 'requests' && <BarRequests />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;

